"use client";

import { AlertCircle, FileText, Upload } from "lucide-react";
import { useCallback, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DistrictData } from "@/types/review";

interface CsvUploaderProps {
  onDataLoaded: (data: DistrictData[]) => void;
  onError: (error: string) => void;
}

export default function CsvUploader({
  onDataLoaded,
  onError,
}: CsvUploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const parseCSV = useCallback((csvText: string): DistrictData[] => {
    const lines = csvText.trim().split("\n");
    if (lines.length < 2) {
      throw new Error(
        "CSV file must contain at least a header row and one data row",
      );
    }

    const headers =
      lines[0]?.split(",").map((h) => h.trim().replace(/"/g, "")) || [];

    // Validate required headers
    const requiredHeaders = [
      "nces_id",
      "district_name",
      "state_abbreviation",
      "district_website",
      "current_ats_url",
      "job_boards_count",
      "search_timestamp",
      "ats_url_1",
      "ats_url_2",
    ];

    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header),
    );
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
    }

    const districts: DistrictData[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]?.trim() || "";
      if (!line) continue;

      const values = parseCSVLine(line);

      if (values.length >= headers.length) {
        const district: DistrictData = {
          nces_id: values[0] || "",
          district_name: values[1] || "",
          state_abbreviation: values[2] || "",
          district_website: values[3] || "",
          current_ats_url: values[4] || "",
          job_boards_count: Number(values[5]) || 0,
          search_timestamp: values[6] || "",
          ats_url_1: values[7] || "",
          ats_url_2: values[8] || "",
        };

        districts.push(district);
      }
    }

    if (districts.length === 0) {
      throw new Error("No valid data rows found in CSV file");
    }

    return districts;
  }, []);

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ""));
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current.trim().replace(/^"|"$/g, ""));
    return result;
  };

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith(".csv")) {
        onError("Please upload a CSV file (.csv extension required)");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        onError("File size too large. Please upload a file smaller than 10MB");
        return;
      }

      setIsProcessing(true);

      try {
        const text = await file.text();
        const districts = parseCSV(text);
        onDataLoaded(districts);
      } catch (error) {
        onError(
          error instanceof Error ? error.message : "Failed to parse CSV file",
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [parseCSV, onDataLoaded, onError],
  );

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Upload District Data CSV
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          } ${isProcessing ? "opacity-50 pointer-events-none" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isProcessing ? (
            <div className="space-y-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600">Processing CSV file...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="h-12 w-12 text-gray-400 mx-auto" />
              <div>
                <p className="text-lg font-medium text-gray-900">
                  Drop your CSV file here, or{" "}
                  <label className="text-blue-600 hover:text-blue-500 cursor-pointer underline">
                    browse to upload
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileInput}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Maximum file size: 10MB
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-600">
              <p className="font-medium">Required CSV format:</p>
              <p className="mt-1">
                Your CSV must include these columns: nces_id, district_name,
                state_abbreviation, district_website, current_ats_url,
                job_boards_count, search_timestamp, ats_url_1, ats_url_2
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
